import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { loadActiveContextSelection } from '../../utils/activeContextSelection'
import { loadDeletedLots } from '../../utils/deletedLots'
import { farmTimeZone, farmToday, farmDayKey } from '../../utils/farmTime'
import { ensureFarmList, type WizardFarm } from '../../utils/farmWizard'
import { feedLogApi } from '../../utils/feedLogApi'
import { fishStockLogApi } from '../../utils/fishStockLogApi'
import { lotApi } from '../../utils/lotApi'
import { mortalityLogApi } from '../../utils/mortalityLogApi'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import {
  buildDailySheetTemplate,
  DAILY_SHEET_COLUMNS,
  parseDailySheet,
  recordKey,
  type DailySheetColumn,
  type DailySheetContext,
  type DailySheetIssue,
  type DailySheetLine,
  type DailySheetRecordKind,
  type DailySheetResult
} from './dailySheet'
import { DAILY_SHEET_SOURCE, saveDailySheetLines, type DailySheetSaveResult } from './dailySheetSave'
import { buildLotPlacements } from './lotPlacements'

// Findings #199: "Hoja diaria" — back-fill older lots from one CSV per
// facility. Lots and moves are made in Lotes first; this only adds the daily
// records, and saves nothing while any line has an error.

const card = { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 } as const

type Loaded = { context: DailySheetContext; facility: WizardFarm }

const loadSheetContext = async (facility: WizardFarm): Promise<DailySheetContext> => {
  const farmId = String(facility.id)
  const timeZone = farmTimeZone(facility)
  const lots = (await lotApi.list({ farmId })).filter((lot) => Boolean(lot.productionUnitId))
  const [events, deletedLots, counts, weights, mortalities, feeds] = await Promise.all([
    lotApi.listLineageEventsForLots(lots.map((lot) => lot.id)).catch(() => []),
    loadDeletedLots(farmId),
    fishStockLogApi.list({ farmId }).catch(() => []),
    weightSampleLogApi.list({ farmId }).catch(() => []),
    mortalityLogApi.list({ farmId }).catch(() => []),
    feedLogApi.list({ farmId }).catch(() => [])
  ])
  const placementsByLotId = new Map(lots.map((lot) => [lot.id, buildLotPlacements(lot, events, timeZone)] as const))
  const existingRecordKeys = new Set<string>()
  const addKeys = (kind: DailySheetRecordKind, logs: ReadonlyArray<{ productionUnitId: string; lotId?: string; recordedAt: string; metadata?: Record<string, unknown> }>) => {
    logs.forEach((log) => {
      if (log.metadata?.source === DAILY_SHEET_SOURCE || !log.lotId) return
      const day = farmDayKey(log.recordedAt, timeZone)
      if (day) existingRecordKeys.add(recordKey(kind, log.productionUnitId, log.lotId, day))
    })
  }
  addKeys('count', counts)
  addKeys('weight', weights)
  addKeys('mortality', mortalities)
  addKeys('feed', feeds)
  return {
    timeZone,
    today: farmToday(timeZone),
    facilityName: String(facility.name ?? ''),
    units: (facility.productionUnits ?? []).map((unit) => ({ id: String(unit.id), name: String(unit.name ?? '') })),
    lots: lots.map((lot) => ({ id: lot.id, lotKey: lot.lotKey, productionUnitId: lot.productionUnitId })),
    placementsByLotId,
    deletedLots: deletedLots ?? [],
    existingRecordKeys
  }
}

export default function DailySheetImport(props: { canImport: boolean }) {
  const t = useT()
  const [facilities, setFacilities] = useState<WizardFarm[]>([])
  const [farmId, setFarmId] = useState('')
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [fileText, setFileText] = useState('')
  const [onlyProblems, setOnlyProblems] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<DailySheetSaveResult | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void ensureFarmList().then((list) => {
      if (cancelled) return
      const farms = list.filter((farm) => farm.facilityType !== 'processing-plant')
      setFacilities(farms)
      const remembered = loadActiveContextSelection().farmId
      setFarmId((current) => current || (farms.some((farm) => String(farm.id) === remembered) ? remembered : String(farms[0]?.id ?? '')))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  const reload = (facility: WizardFarm | undefined) => {
    setLoaded(null)
    setLoadError(null)
    if (!facility) return () => {}
    let cancelled = false
    setLoading(true)
    void loadSheetContext(facility)
      .then((context) => { if (!cancelled) setLoaded({ context, facility }) })
      .catch((error) => { if (!cancelled) setLoadError(error instanceof Error ? error.message : String(error)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }

  const facility = facilities.find((farm) => String(farm.id) === farmId)
  useEffect(() => reload(facility), [facility])

  const result: DailySheetResult | null = useMemo(
    () => (loaded && fileText ? parseDailySheet(fileText, loaded.context) : null),
    [loaded, fileText]
  )

  const headers = useMemo(() => Object.fromEntries(
    DAILY_SHEET_COLUMNS.map((column) => [column, t(`import.daily.column.${column}` as TranslationKey)])
  ) as Record<DailySheetColumn, string>, [t])

  const downloadTemplate = () => {
    const text = buildDailySheetTemplate(headers, [
      ['2026-09-09', 'R7', '26SETE1', '20428', t('import.daily.method.exact'), '', '', '', t('import.daily.template.arrival')],
      ['2026-09-10', 'R7', '26SETE1', '', '', '', '12', '0.5', ''],
      ['2026-09-20', 'R7', '26SETE1', '', '', '0.35', '8', '0.7', t('import.daily.template.sample')]
    ])
    const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = t('import.daily.template.fileName')
    link.click()
    URL.revokeObjectURL(url)
  }

  const readFile = async (file: File | undefined) => {
    setSaved(null)
    setSaveError(null)
    if (!file) {
      setFileName('')
      setFileText('')
      return
    }
    setFileName(file.name)
    setFileText(await file.text())
  }

  const issueText = (issue: DailySheetIssue): string => {
    const params = { ...(issue.params ?? {}) }
    if (issue.code === 'missingColumn' && params.column) params.column = headers[params.column as DailySheetColumn] ?? params.column
    if (issue.code === 'alreadyOnScreens' && params.kinds) {
      params.kinds = params.kinds.split(',').map((kind) => t(`import.daily.kind.${kind}` as TranslationKey)).join(', ')
    }
    return t(`import.daily.issue.${issue.code}` as TranslationKey, params)
  }

  const save = async () => {
    if (!result || !loaded) return
    setSaving(true)
    setSaveError(null)
    try {
      const units = new Map(loaded.context.units.map((unit) => [unit.id, unit.name] as const))
      const outcome = await saveDailySheetLines(result.lines, {
        farmId: String(loaded.facility.id),
        farmName: String(loaded.facility.name ?? ''),
        timeZone: loaded.context.timeZone,
        unitName: (unitId) => units.get(unitId) ?? ''
      })
      setSaved(outcome)
      setFileText('')
      setFileName('')
      reload(loaded.facility)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error))
    } finally {
      setSaving(false)
    }
  }

  const lineValues = (line: DailySheetLine): string => [
    line.raw.count ? `${t('import.daily.kind.count')} ${line.raw.count}${line.raw.method ? ` (${line.raw.method})` : ''}` : '',
    line.raw.weight ? `${t('import.daily.kind.weight')} ${line.raw.weight} g` : '',
    line.raw.mortality ? `${t('import.daily.kind.mortality')} ${line.raw.mortality}` : '',
    line.raw.feed ? `${t('import.daily.kind.feed')} ${line.raw.feed} kg` : ''
  ].filter(Boolean).join(' · ')

  const visibleLines = result
    ? (onlyProblems && (result.totals.errorLines > 0 || result.lines.some((line) => line.warnings.length > 0))
        ? result.lines.filter((line) => line.errors.length > 0 || line.warnings.length > 0)
        : result.lines)
    : []
  const canSave = Boolean(result && props.canImport && !saving && result.headerErrors.length === 0
    && result.totals.errorLines === 0 && result.totals.okLines > 0)
  const blockedSync = saved?.sync.reduce((sum, status) => sum + status.blockedCount, 0) ?? 0
  const offline = saved?.sync.some((status) => status.syncState === 'offline') ?? false

  return (
    <div style={{ display: 'grid', gap: 16 }} data-testid="daily-sheet-import">
      <div style={{ ...card, background: '#f0f9ff', borderColor: '#bae6fd' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#0c4a6e' }}>{t('import.daily.before.title')}</h2>
        <ol style={{ margin: 0, paddingLeft: 20, color: '#0f172a', lineHeight: 1.7 }}>
          <li>{t('import.daily.before.step1')} <Link to="/lots">{t('import.daily.before.lotsLink')}</Link></li>
          <li>{t('import.daily.before.step2')}</li>
          <li>{t('import.daily.before.step3')}</li>
        </ol>
        <p style={{ margin: '8px 0 0', color: '#475569', fontSize: 13 }}>{t('import.daily.before.noLots')}</p>
      </div>

      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <label style={{ display: 'grid', gap: 6, maxWidth: 360 }}>
          <span style={{ fontWeight: 600 }}>{t('import.daily.facility')}</span>
          <select value={farmId} onChange={(event) => { setFarmId(event.target.value); setSaved(null) }} style={{ padding: 8, borderRadius: 8 }}>
            {facilities.map((farm) => <option key={String(farm.id)} value={String(farm.id)}>{farm.name}</option>)}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={downloadTemplate} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0f766e', background: '#fff', color: '#0f766e', cursor: 'pointer', fontWeight: 600 }}>
            {t('import.daily.template.download')}
          </button>
          <span style={{ fontSize: 13, color: '#475569' }}>{t('import.daily.template.help')}</span>
        </div>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>{t('import.daily.file')}</span>
          <input type="file" accept=".csv,text/csv" disabled={!loaded} onChange={(event) => { void readFile(event.target.files?.[0]); event.target.value = '' }} data-testid="daily-sheet-file" />
          <span style={{ fontSize: 12, color: '#64748b' }}>{fileName ? t('import.daily.fileLoaded', { name: fileName }) : t('import.daily.fileHelp')}</span>
        </label>
        {loading ? <div style={{ color: '#475569' }}>{t('import.daily.loading')}</div> : null}
        {loadError ? <div role="alert" style={{ color: '#b91c1c' }}>{t('import.daily.loadError', { reason: loadError })}</div> : null}
        {!props.canImport ? <div style={{ color: '#9a3412' }}>{t('import.daily.readOnly')}</div> : null}
      </div>

      {saved ? (
        <div role="status" style={{ ...card, background: '#ecfdf5', borderColor: '#a7f3d0' }} data-testid="daily-sheet-saved">
          <strong>{t('import.daily.saved.title')}</strong>
          <div style={{ marginTop: 6 }}>{t('import.daily.saved.summary', {
            counts: String(saved.counts),
            weights: String(saved.weights),
            mortalities: String(saved.mortalities),
            feeds: String(saved.feeds)
          })}</div>
          <div style={{ marginTop: 6, color: blockedSync > 0 ? '#b91c1c' : '#475569' }}>
            {blockedSync > 0 ? t('import.daily.saved.blocked', { count: String(blockedSync) }) : offline ? t('import.daily.saved.offline') : t('import.daily.saved.synced')}
          </div>
          <div style={{ marginTop: 6 }}>{t('import.daily.saved.review')} <Link to="/biomass">{t('import.daily.saved.biomass')}</Link> · <Link to="/database">{t('import.daily.saved.database')}</Link></div>
        </div>
      ) : null}
      {saveError ? <div role="alert" style={{ ...card, color: '#b91c1c' }}>{t('import.daily.saveError', { reason: saveError })}</div> : null}

      {result ? (
        <div style={{ ...card, display: 'grid', gap: 12 }} data-testid="daily-sheet-preview">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{t('import.daily.preview.title')}</h2>
          {result.headerErrors.length > 0 ? (
            <ul role="alert" style={{ margin: 0, color: '#b91c1c' }}>
              {result.headerErrors.map((issue, index) => <li key={index}>{issueText(issue)}</li>)}
            </ul>
          ) : (
            <>
              <div data-testid="daily-sheet-totals">{t('import.daily.preview.totals', {
                lines: String(result.lines.length),
                counts: String(result.totals.count),
                weights: String(result.totals.weight),
                mortalities: String(result.totals.mortality),
                feeds: String(result.totals.feed),
                errors: String(result.totals.errorLines)
              })}</div>
              {result.totals.errorLines > 0 ? (
                <div role="alert" style={{ padding: 10, borderRadius: 8, background: '#fef2f2', color: '#991b1b' }}>
                  {t('import.daily.preview.fixFirst')}
                </div>
              ) : null}
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={onlyProblems} onChange={(event) => setOnlyProblems(event.target.checked)} />
                {t('import.daily.preview.onlyProblems')}
              </label>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: 6 }}>{t('import.daily.preview.line')}</th>
                      <th style={{ padding: 6 }}>{headers.date}</th>
                      <th style={{ padding: 6 }}>{headers.unit}</th>
                      <th style={{ padding: 6 }}>{headers.lot}</th>
                      <th style={{ padding: 6 }}>{t('import.daily.preview.values')}</th>
                      <th style={{ padding: 6 }}>{t('import.daily.preview.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLines.map((line) => (
                      <tr key={line.lineNumber} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }} data-line-status={line.errors.length > 0 ? 'error' : line.warnings.length > 0 ? 'warning' : 'ok'}>
                        <td style={{ padding: 6 }}>{line.lineNumber}</td>
                        <td style={{ padding: 6 }}>{line.raw.date}</td>
                        <td style={{ padding: 6 }}>{line.raw.unit}</td>
                        <td style={{ padding: 6 }}>{line.raw.lot}</td>
                        <td style={{ padding: 6 }}>{lineValues(line)}</td>
                        <td style={{ padding: 6 }}>
                          {line.errors.length > 0
                            ? line.errors.map((issue, index) => <div key={index} style={{ color: '#b91c1c' }}>✗ {issueText(issue)}</div>)
                            : <div style={{ color: '#047857' }}>✓ {t('import.daily.preview.ready')}</div>}
                          {line.warnings.map((issue, index) => <div key={`w${index}`} style={{ color: '#a16207' }}>⚠ {issueText(issue)}</div>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <button
                  type="button"
                  disabled={!canSave}
                  onClick={() => void save()}
                  data-testid="daily-sheet-save"
                  style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: canSave ? '#0f766e' : '#94a3b8', color: '#fff', fontWeight: 700, cursor: canSave ? 'pointer' : 'not-allowed' }}
                >
                  {saving ? t('import.daily.saving') : t('import.daily.save', { count: String(result.totals.okLines) })}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}
